import * as React from "react";
import { X, Download, Monitor } from "lucide-react";
import { Button } from "@repo/ui";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEY = "pwa-install-dismissed";
const DISMISSAL_DAYS = 7;

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { until } = JSON.parse(raw) as { until: number };
    return Date.now() < until;
  } catch {
    return false;
  }
}

function setDismissed(permanent: boolean): void {
  try {
    const until = permanent
      ? Date.now() + 365 * 24 * 60 * 60 * 1000 // 1 year
      : Date.now() + DISMISSAL_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ until }));
  } catch {
    // Ignore storage errors
  }
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    React.useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [isInstalling, setIsInstalling] = React.useState(false);

  React.useEffect(() => {
    // Don't show if already running as installed PWA
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }
    // Don't show if user previously dismissed
    if (isDismissed()) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Small delay so the prompt doesn't appear immediately on page load
      timeoutId = setTimeout(() => setIsVisible(true), 3000);
    };

    const appInstalledHandler = () => {
      setIsInstalled(true);
      setIsVisible(false);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Listen for successful install
    window.addEventListener("appinstalled", appInstalledHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", appInstalledHandler);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
      } else {
        // User dismissed the native prompt — treat as temporary dismissal
        setDismissed(false);
      }
    } finally {
      setIsVisible(false);
      setDeferredPrompt(null);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(false);
    setIsVisible(false);
  };

  const handleNeverShow = () => {
    setDismissed(true);
    setIsVisible(false);
  };

  if (isInstalled || !isVisible || !deferredPrompt) return null;

  return (
    <div
      role="dialog"
      aria-label="Install C2 Vault app"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 sm:bottom-6 sm:left-auto sm:right-6 sm:translate-x-0"
    >
      <div className="relative overflow-hidden rounded-xl border border-border bg-surface shadow-lg ring-1 ring-black/5">
        {/* Brand accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-primary to-secondary" />

        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 overflow-hidden rounded-xl border border-border/50">
              <img
                src="/icons/icon-72x72.png"
                alt="C2 Vault"
                width={48}
                height={48}
                className="block"
              />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                Install C2 Vault
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-text-secondary">
                Add to your home screen for faster access to your compliance
                dashboard.
              </p>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss install prompt"
              className="flex-shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-section-bg hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Browser hints */}
          <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Monitor className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Works in Chrome &amp; Edge</span>
          </div>

          {/* Actions */}
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={handleInstall}
              disabled={isInstalling}
              size="sm"
              className="flex-1 gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              {isInstalling ? "Installing\u2026" : "Install App"}
            </Button>
            <button
              type="button"
              onClick={handleNeverShow}
              className="rounded text-xs text-muted-foreground underline-offset-2 hover:text-text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Don&apos;t ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
